import { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { Paper } from '@mui/material';

import useSimpleTable from '../../../hooks/useSimpleTable';
import { BasicFlex } from '../../../utils/Common';
import SimplePagination from '../../../utils/utilities/SimplePagination';
import { useFormData } from '../../../contexts/FormData';
import { useFormikContext } from 'formik';

import { formatPhoneNumber } from '../../../utils/util';

import useIsMobile from '../../../hooks/useIsMobile'

import { useQuery } from '@tanstack/react-query';

import { getContactList } from '../apiService';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: 1rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid #ddd;
  font-weight: 600;
`;

const CheckHead = styled(Th)`
  width: 40px;
  text-align: center;
`;

const Row = styled.tr`
  cursor: pointer;
  &:hover { background: #f7f7f7; }
  &[data-selected='true'] { background: #e8f0fe; }
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
`;

const CheckCell = styled(Td)`
  width: 40px;
  text-align: center;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
`;

const Name = styled.span`
  font-weight: 500;
`;

const Phone = styled.span`
  color: #666;
  font-size: 12px;
`;

/* ---- collapsible selected-contacts panel ---- */
const SelectedPanel = styled.div`
  align-self: flex-start;
  width: 100%;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const PanelHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #fafafa;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  &:hover { background: #f3f3f3; }
`;

const HeaderLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 10px;
  background: #1a73e8;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const Chevron = styled.span`
  color: #888;
  font-size: 12px;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.open ? '90deg' : '0deg')});
`;

const PanelBody = styled.div`
  max-height: 360px;
  overflow-y: auto;
  padding: 8px 10px 12px;
  border-top: 1px solid #eee;
`;

const Empty = styled.div`
  color: #999;
  font-size: 12px;
  padding: 6px 4px;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1px 10px;
`;

const Contact = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 3px 18px 3px 6px;
  border-radius: 4px;
  line-height: 1.25;
  &:hover { background: #f3f6fb; }
  &:hover .remove-btn { opacity: 1; }
`;

const CName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CPhone = styled.span`
  font-size: 11px;
  color: #888;
`;

const Remove = styled.button`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #bbb;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px;
  opacity: 0;
  transition: opacity 0.1s ease;
  &:hover { color: #d33; }
`;

const CustomerList = () => {
  const { formValues, setFormValues } = useFormData();
  const { values } = useFormikContext() || {};

  const { data, pagination, setPagination } = useSimpleTable({
    url: 'customers',
    defaultSort: 'full_name',
    rowsPerPage: 200,
  });

  const { data: existingContacts } = useQuery({
    queryKey: ['contact_groups', values?.contact_group_id, 'contacts'],
    queryFn: () => getContactList(values?.contact_group_id),
    enabled: !!values?.contact_group_id
  })

  const { isTablet } = useIsMobile();

  const selectAllRef = useRef(null);
  const initializedRef = useRef(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const pageRows = data?.rows || [];
  const contacts = formValues.contacts || [];
  const selectedIds = new Set(contacts.map((c) => c.customer_id));

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r.customer_id));
  const someOnPageSelected = pageRows.some((r) => selectedIds.has(r.customer_id));

  // native checkboxes have no React prop for indeterminate
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  // edit mode: seed selected contacts from the existing group once
  useEffect(() => {
    if (initializedRef.current) return;
    if (!values?.contact_group_id || !existingContacts) return;

    initializedRef.current = true;
    setFormValues((prev) => {
      const prevContacts = prev.contacts || [];
      const existingIds = new Set(prevContacts.map((c) => c.customer_id));
      const fromServer = existingContacts
        .filter((c) => !existingIds.has(c.customer_id))
        .map((c) => ({
          customer_id: c.customer_id,
          full_name: c.full_name,
          mobile_phone: c.mobile_phone,
        }));
      return { ...prev, contacts: [...prevContacts, ...fromServer] };
    });
  }, [existingContacts, values?.contact_group_id, setFormValues]);

  const toggleContact = (row) => {
    setFormValues((prev) => {
      const prevContacts = prev.contacts || [];
      const exists = prevContacts.some((c) => c.customer_id === row.customer_id);
      return {
        ...prev,
        contacts: exists
          ? prevContacts.filter((c) => c.customer_id !== row.customer_id)
          : [
              ...prevContacts,
              {
                customer_id: row.customer_id,
                full_name: row.full_name,
                mobile_phone: row.mobile_phone,
              },
            ],
      };
    });
  };

  const removeContact = (customerId) => {
    setFormValues((prev) => ({
      ...prev,
      contacts: (prev.contacts || []).filter((c) => c.customer_id !== customerId),
    }));
  };

  const toggleSelectAll = () => {
    setFormValues((prev) => {
      const prevContacts = prev.contacts || [];
      if (allOnPageSelected) {
        const pageIds = new Set(pageRows.map((r) => r.customer_id));
        return {
          ...prev,
          contacts: prevContacts.filter((c) => !pageIds.has(c.customer_id)),
        };
      }
      const existing = new Set(prevContacts.map((c) => c.customer_id));
      const toAdd = pageRows
        .filter((r) => !existing.has(r.customer_id))
        .map((r) => ({
          customer_id: r.customer_id,
          full_name: r.full_name,
          mobile_phone: r.mobile_phone,
        }));
      return { ...prev, contacts: [...prevContacts, ...toAdd] };
    });
  };

  return (
    <BasicFlex align="flex-start" gap="2rem" dir={isTablet ? "column-reverse" : "row"}>
      <Paper variant="outlined" sx={{width: isTablet ? "100%" : "50%", py: "1rem", px: ".5rem"}}>
      <BasicFlex dir="column">
        <Table>
          <thead>
            <tr>
              <CheckHead>
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  checked={allOnPageSelected}
                  onChange={toggleSelectAll}
                />
              </CheckHead>
              <Th>Customer</Th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const isSelected = selectedIds.has(row.customer_id);
              return (
                <Row
                  key={row.customer_id}
                  data-selected={isSelected}
                  onClick={() => toggleContact(row)}
                >
                  <CheckCell>
                    <input type="checkbox" checked={isSelected} readOnly />
                  </CheckCell>
                  <Td>
                    <Stack>
                      <Name>{row.full_name}</Name>
                      <Phone>{formatPhoneNumber(row.mobile_phone) || '—'}</Phone>
                    </Stack>
                  </Td>
                </Row>
              );
            })}
          </tbody>
        </Table>

        <SimplePagination
          rowsPerPage={pagination?.rowsPerPage}
          currentPage={pagination?.page}
          setPagination={setPagination}
          resultCount={data?.count}
          results={data?.rows}
          customRPP={[100, 200, 500]}
        />
      </BasicFlex>
      </Paper>

      <BasicFlex dir="column" width={isTablet ? "100%" : "50%"}>
        <SelectedPanel>
          <PanelHeader type="button" onClick={() => setPanelOpen((o) => !o)}>
            <HeaderLabel>
              Selected contacts
              <CountBadge>{contacts.length}</CountBadge>
            </HeaderLabel>
            <Chevron open={panelOpen}>▶</Chevron>
          </PanelHeader>

          {panelOpen && (
            <PanelBody>
              {contacts.length === 0 ? (
                <Empty>No contacts selected.</Empty>
              ) : (
                <ContactGrid>
                  {contacts.map((c) => (
                    <Contact key={c.customer_id}>
                      <CName title={c.full_name}>{c.full_name}</CName>
                      <CPhone>{formatPhoneNumber(c.mobile_phone) || '—'}</CPhone>
                      <Remove
                        type="button"
                        className="remove-btn"
                        title="Remove"
                        onClick={() => removeContact(c.customer_id)}
                      >
                        ×
                      </Remove>
                    </Contact>
                  ))}
                </ContactGrid>
              )}
            </PanelBody>
          )}
        </SelectedPanel>
      </BasicFlex>
    </BasicFlex>
  );
};

export default CustomerList;